import React, { useEffect, useMemo } from "react";
import QuoteWizard from "./components/QuoteWizard";
import { MAX_UTM_LENGTH } from "./components/formSchema";
import AppLayout from "./AppLayout";
import HeroLeft from "./components/HeroLeft";
import { surfaceRangeToNumeric } from "./domain/surfaceRanges";
import useEmbedHeight from "./hooks/useEmbedHeight";
import "./App.css";

const RESPONSE_BY_HTTP_STATUS = {
  400: { success: false, status: "invalid_request", retryable: false },
  403: { success: false, status: "forbidden", retryable: false },
  409: { success: false, status: "conflict", retryable: false },
  413: { success: false, status: "invalid_request", retryable: false },
  415: { success: false, status: "invalid_request", retryable: false },
  503: { success: false, status: "unavailable", retryable: true },
  504: { success: false, status: "unknown", retryable: true },
};

function useQueryParams() {
  return useMemo(() => new URLSearchParams(window.location.search), []);
}

function useEmbedMode(params) {
  return useMemo(() => {
    if (params.get("embed") === "1") return true;
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }, [params]);
}

function normalizeCoordinate(value) {
  if (value === null || value === "" || typeof value === "boolean") return null;
  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
}

function normalizeUtm(value) {
  const normalized = typeof value === "string" ? value.trim().slice(0, MAX_UTM_LENGTH) : "";
  return normalized || undefined;
}

export function readUrlAttribution(params) {
  return {
    utm_source: normalizeUtm(params.get("utm_source")) || "",
    utm_medium: normalizeUtm(params.get("utm_medium")) || "",
    utm_campaign: normalizeUtm(params.get("utm_campaign")) || "",
  };
}

export function normalizePayload({
  formData,
  submissionId,
  turnstileToken,
  website,
}) {
  const surfaceRange = formData.surface_range;
  const payload = {
    submission_id: submissionId,
    projet: formData.projet,
    prestations: [...formData.prestations],
    demande_libre:
      formData.projet === "Autre"
        ? String(formData.demande_libre || "").trim()
        : "",
    type_bien: formData.type_bien,
    adresse: String(formData.adresse || "").trim().replace(/\s+/g, " "),
    adresse_normalisee: String(formData.adresse_normalisee || "").trim(),
    code_postal: String(formData.code_postal || "").trim(),
    ville: String(formData.ville || "").trim(),
    code_insee: String(formData.code_insee || "").trim().toUpperCase(),
    latitude: normalizeCoordinate(formData.latitude),
    longitude: normalizeCoordinate(formData.longitude),
    surface_range: {
      label: surfaceRange.label,
      min: surfaceRange.min,
      max: surfaceRange.max,
    },
    surface_m2: surfaceRangeToNumeric(surfaceRange),
    civilite: formData.civilite,
    prenom: String(formData.prenom || "").trim(),
    nom: String(formData.nom || "").trim(),
    telephone: String(formData.telephone || "").replace(/\s/g, ""),
    email: String(formData.email || "").trim().toLowerCase(),
    turnstile_token: turnstileToken,
    website: String(website || ""),
  };

  const utmSource = normalizeUtm(formData.utm_source);
  const utmMedium = normalizeUtm(formData.utm_medium);
  const utmCampaign = normalizeUtm(formData.utm_campaign);
  if (utmSource) payload.utm_source = utmSource;
  if (utmMedium) payload.utm_medium = utmMedium;
  if (utmCampaign) payload.utm_campaign = utmCampaign;

  return payload;
}

async function readSafeJson(response) {
  try {
    const value = await response.json();
    return value && typeof value === "object" && !Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
}

function getBrowserFetch() {
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return undefined;
  }

  return window.fetch.bind(window);
}

export async function submitQuoteRequest(request, fetchImpl = getBrowserFetch()) {
  const payload = normalizePayload(request);
  let response;

  try {
    if (typeof fetchImpl !== "function") {
      throw new Error("browser_fetch_unavailable");
    }
    response = await fetchImpl("/api/quote-request", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify(payload),
    });
  } catch {
    return { success: false, status: "unavailable", retryable: true };
  }

  if (!response || typeof response.status !== "number") {
    return { success: false, status: "unavailable", retryable: true };
  }

  if (response.status === 429) {
    return { success: false, status: "rate_limited", retryable: true };
  }

  const body = await readSafeJson(response);
  if (
    response.status === 201 &&
    body?.success === true &&
    body.status === "created"
  ) {
    return { success: true, status: "created", retryable: false };
  }
  if (
    response.status === 200 &&
    body?.success === true &&
    body.status === "exists"
  ) {
    return { success: true, status: "exists", retryable: false };
  }

  if (response.status === 400 && body?.status === "anti_bot_failed") {
    return { success: false, status: "anti_bot_failed", retryable: true };
  }
  if (response.status === 503 && body?.status === "security_unavailable") {
    return { success: false, status: "security_unavailable", retryable: true };
  }

  return (
    RESPONSE_BY_HTTP_STATUS[response.status] || {
      success: false,
      status: "unavailable",
      retryable: true,
    }
  );
}

export default function App({
  turnstileSiteKey = process.env.REACT_APP_NOUVAC_QUOTE_FORM_TURNSTILE_SITE_KEY,
  turnstileApiLoader,
  uuidFactory,
  now,
}) {
  const params = useQueryParams();
  const isEmbed = useEmbedMode(params);
  const initialUtm = useMemo(() => readUrlAttribution(params), [params]);

  useEffect(() => {
    if (isEmbed) {
      document.documentElement.classList.add("embed-mode");
      document.body.classList.add("embed-mode");
    } else {
      document.documentElement.classList.remove("embed-mode");
      document.body.classList.remove("embed-mode");
    }

    return () => {
      document.documentElement.classList.remove("embed-mode");
      document.body.classList.remove("embed-mode");
    };
  }, [isEmbed]);

  useEmbedHeight(isEmbed);

  async function handleSubmit(request) {
    return submitQuoteRequest(request);
  }

  return (
    <AppLayout>
      <div className={`app ${isEmbed ? "embed" : ""}`}>
        <div className="page-shell">
          <div className="hero-layout">
            <HeroLeft />

            <section className="right-panel">
              <div className="wizard-card">
                <QuoteWizard
                  onSubmit={handleSubmit}
                  initialUtm={initialUtm}
                  turnstileSiteKey={turnstileSiteKey}
                  turnstileApiLoader={turnstileApiLoader}
                  uuidFactory={uuidFactory}
                  now={now}
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
